import React from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import Layout from '../../components/Layout/Layout';
import {
  MdUploadFile,
  MdSmartToy,
  MdArrowForward,
  MdCheckCircle,
  MdCloudUpload,
} from '../../utils/icons';
import { useAuthorization } from '../../auth/useAuthorization';
import { Roles, Role } from '../../auth/roles';
import './Dashboard.css';

interface QuickAction {
  to: string;
  icon: React.ComponentType<any>;
  title: string;
  desc: string;
  tags: string[];
  accent: 'blue' | 'purple' | 'neutral';
  /** Card is shown only when the user holds this role. */
  role: Role;
}

const ACTIONS: QuickAction[] = [
  {
    to: '/step-productlinq',
    icon: MdUploadFile,
    title: 'Product Data Onboarding',
    desc: "Drop a supplier Excel, CSV or PDF and extract it into mapped STEP product data — then post straight to STEP.",
    tags: ['Extraction', 'STEP mapping'],
    accent: 'blue',
    role: Roles.StepProductLinq,
  },
  {
    to: '/product-agent',
    icon: MdSmartToy,
    title: 'Product Support AI Agent',
    desc: 'Ask questions about products, specs and availability. Get grounded answers from your catalogue in plain language.',
    tags: ['Q&A', 'Catalogue'],
    accent: 'purple',
    role: Roles.ProductSupportAgent,
  },
];

const Dashboard: React.FC = () => {
  const { accounts } = useMsal();
  const { hasRole } = useAuthorization();
  const account = accounts[0];
  const firstName = account?.name?.split(' ')[0] ?? 'User';
  const visibleActions = ACTIONS.filter((a) => hasRole(a.role));

  return (
    <Layout title="Dashboard">
      <div className="dash-home">
        <h2 className="dash-home-title">Good day, {firstName}</h2>
        <p className="dash-home-sub">Pick a tool to get started — here's what OmniLinQ can do for you today.</p>

        <div className="dash-cards">
          {visibleActions.map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.title} to={a.to} className={`dash-card dash-card--${a.accent}`}>
                <div className="dash-card-top">
                  <div className="dash-card-icon"><Icon aria-hidden="true" /></div>
                  <MdArrowForward aria-hidden="true" className="dash-card-arrow" />
                </div>
                <div>
                  <h3 className="dash-card-title">{a.title}</h3>
                  <p className="dash-card-desc">{a.desc}</p>
                </div>
                <div className="dash-card-tags">
                  {a.tags.map((t) => <span key={t} className="dash-card-tag">{t}</span>)}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
